// src/pages/CourseTime/hooks/useDiscussion.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchDiscussionMessages, postDiscussionMessage, updateDiscussionMessage, deleteDiscussionMessage, updateContentHistory } from '../../../services/learning_program.service';
// Mock APIs for UI development (commented out)
// import { fetchDiscussionMessagesMock } from '../../../services/_mock_api/fetch_discussion_messages.mock';
// import { postDiscussionMessageMock } from '../../../services/_mock_api/post_discussion_message.mock';

export const useDiscussion = (moduleCode, profileId, lastReadTimestamp = null, onUpdateLastRead = null, discussionSessions = []) => {
  // Discussion state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [messagesPerSession, setMessagesPerSession] = useState({}); // Object with slide_id as key
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedMessages, setHasLoadedMessages] = useState({});
  
  // Use the prop directly instead of local state to avoid sync issues
  const lastReadMessageTimestamp = lastReadTimestamp;
  
  // Refs for auto-scroll and input management
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Constants
  const MAX_MESSAGE_LENGTH = 1000;
  const REFRESH_INTERVAL_OPEN = 15000; // 15 seconds when modal is open
  const REFRESH_INTERVAL_CLOSED = 30000; // 30 seconds when modal is closed
  
  // Auto-scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);
  
  // Format timestamp for display
  const formatMessageTime = useCallback((timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }, []);
  
  // Get user initials for avatar
  const getUserInitials = useCallback((name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, []);
  
  // Update last read message timestamp
  const updateLastReadTimestamp = useCallback(async () => {
    if (!moduleCode || !profileId) return;
    
    const currentTimestamp = new Date().toISOString();
    
    try {
      // Update the timestamp in content history
      await updateContentHistory(moduleCode, profileId, {
        last_message_read: currentTimestamp
      });
      
      // Notify parent component to update its state
      if (onUpdateLastRead) {
        onUpdateLastRead(currentTimestamp);
      }
      
    } catch (err) {
      console.error('Error updating last read timestamp:', err);
    }
  }, [moduleCode, profileId, onUpdateLastRead]);
  
  // Get current active session
  const activeSession = discussionSessions[activeTabIndex];
  const activeSlideId = activeSession?.slide_id;
  const activeMessages = messagesPerSession[activeSlideId] || [];

  // Fetch all messages and separate by slide_id
  const fetchMessages = useCallback(async () => {
    if (!moduleCode) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchDiscussionMessages(moduleCode);
      // For UI development, use: await fetchDiscussionMessagesMock(moduleCode);
      
      if (response.success) {
        const allMessages = response.data.messages || [];
        
        // Group messages by slide_id
        const messagesBySlideId = {};
        allMessages.forEach(message => {
          const slideId = message.slide_id;
          if (!messagesBySlideId[slideId]) {
            messagesBySlideId[slideId] = [];
          }
          messagesBySlideId[slideId].push(message);
        });
        
        // Update messages per session
        setMessagesPerSession(messagesBySlideId);
        
        // Mark all sessions as loaded
        const loadedSessions = {};
        discussionSessions.forEach(session => {
          loadedSessions[session.slide_id] = true;
        });
        setHasLoadedMessages(loadedSessions);
        
        // If modal is open, update last read timestamp (user is actively viewing)
        if (isModalOpen) {
          updateLastReadTimestamp();
        }
        
        // Auto-scroll to bottom after loading messages
        setTimeout(scrollToBottom, 100);
      } else {
        throw new Error(response.message || 'Failed to fetch messages');
      }
    } catch (err) {
      console.error('Error fetching discussion messages:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [moduleCode, discussionSessions, scrollToBottom, isModalOpen, updateLastReadTimestamp]);
  
  // Post new message
  const postMessage = useCallback(async () => {
    if (!newMessage.trim() || !moduleCode || posting || !activeSlideId) return;
    
    const messageText = newMessage.trim();
    if (messageText.length > MAX_MESSAGE_LENGTH) {
      setError(`Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`);
      return;
    }
    
    try {
      setPosting(true);
      setError(null);
      
      const response = await postDiscussionMessage(moduleCode, messageText, activeSlideId);
      // For UI development, use: await postDiscussionMessageMock(moduleCode, messageText, activeSlideId);
      
      if (response.success) {
        // Add new message to the correct session's message list
        const newMsg = response.data.message;
        setMessagesPerSession(prev => ({
          ...prev,
          [activeSlideId]: [...(prev[activeSlideId] || []), newMsg]
        }));
        setNewMessage('');
        
        // Update last read timestamp since user just sent a message
        updateLastReadTimestamp();
        
        // Auto-scroll to bottom after posting
        setTimeout(scrollToBottom, 100);
        
        // Focus back to input
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 200);
      } else {
        throw new Error(response.message || 'Failed to post message');
      }
    } catch (err) {
      console.error('Error posting discussion message:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setPosting(false);
    }
  }, [newMessage, moduleCode, posting, activeSlideId, scrollToBottom, updateLastReadTimestamp]);
  
  // Update existing message
  const updateMessage = useCallback(async (messageId, newMessageText) => {
    if (!newMessageText.trim() || !moduleCode || !messageId) return;
    
    const messageText = newMessageText.trim();
    if (messageText.length > MAX_MESSAGE_LENGTH) {
      setError(`Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`);
      return;
    }
    
    try {
      setError(null);
      
      const response = await updateDiscussionMessage(messageId, messageText);

      if (response.data && response.data.success) {
        // Update the message in the local state
        setMessagesPerSession(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(slideId => {
            updated[slideId] = updated[slideId].map(msg => 
              msg.id === messageId 
                ? { ...response.data.message}
                : msg
            );
          });
          return updated;
        });
        
        // Auto-scroll to bottom after updating
        setTimeout(scrollToBottom, 100);
      } else {
        throw new Error('Failed to update message');
      }
    } catch (err) {
      console.error('Error updating discussion message:', err);
      setError(err.message || 'Failed to update message');
    }
  }, [moduleCode, scrollToBottom]);
  
  // Delete message
  const deleteMessage = useCallback(async (messageId) => {
    if (!moduleCode || !messageId) return;
    
    try {
      setError(null);
      
      const response = await deleteDiscussionMessage(messageId);
      
      if (response.data && response.data.success) {
        // Remove the message from local state
        setMessagesPerSession(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(slideId => {
            updated[slideId] = updated[slideId].filter(msg => msg.id !== messageId);
          });
          return updated;
        });
        
        // Auto-scroll to bottom after deleting
        setTimeout(scrollToBottom, 100);
      } else {
        throw new Error('Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting discussion message:', err);
      setError(err.message || 'Failed to delete message');
    }
  }, [moduleCode, scrollToBottom]);
  
  // Handle input changes
  const handleMessageChange = useCallback((value) => {
    setNewMessage(value);
    setError(null); // Clear error when user starts typing
  }, []);
  
  // Handle Enter key press (Shift+Enter for new line, Enter to send)
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      postMessage();
    }
  }, [postMessage]);
  
  // Tab management
  const switchToTab = useCallback((tabIndex) => {
    setActiveTabIndex(tabIndex);
    setError(null);
    
    // Auto-scroll to bottom when switching tabs
    setTimeout(scrollToBottom, 100);
  }, [scrollToBottom]);

  // Modal handlers
  const openModal = useCallback((targetSlideId = null) => {
    setIsModalOpen(true);
    setError(null);
    
    // If a specific slide_id is provided, switch to that tab
    if (targetSlideId && discussionSessions.length > 0) {
      const targetTabIndex = discussionSessions.findIndex(session => session.slide_id === targetSlideId);
      if (targetTabIndex !== -1) {
        setActiveTabIndex(targetTabIndex);
      }
    }
    
    // Update last read timestamp when user opens modal
    updateLastReadTimestamp();
    
    // Always fetch fresh messages when modal opens (immediate polling)
    if (discussionSessions.length > 0) {
      fetchMessages();
    }
  }, [discussionSessions, fetchMessages, updateLastReadTimestamp]);

  // Special handler for opening modal with newly created session
  const openModalWithNewSession = useCallback((targetSlideId, updatedSessions) => {
    setIsModalOpen(true);
    setError(null);
    
    // Find the tab index in the updated sessions array
    if (targetSlideId && updatedSessions.length > 0) {
      const targetTabIndex = updatedSessions.findIndex(session => session.slide_id === targetSlideId);
      if (targetTabIndex !== -1) {
        setActiveTabIndex(targetTabIndex);
      }
    }
    
    // Update last read timestamp when user opens modal
    updateLastReadTimestamp();
    
    // Always fetch fresh messages when modal opens (immediate polling)
    if (updatedSessions.length > 0) {
      fetchMessages();
    }
  }, [fetchMessages, updateLastReadTimestamp]);
  
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setNewMessage('');
    setError(null);
  }, []);
  
  // Refresh all messages
  const refreshMessages = useCallback(() => {
    if (isModalOpen && moduleCode && discussionSessions.length > 0) {
      fetchMessages();
    }
  }, [isModalOpen, moduleCode, discussionSessions.length, fetchMessages]);
  
  // Auto-refresh messages with different intervals based on modal state
  useEffect(() => {
    if (!moduleCode || discussionSessions.length === 0) return;
    
    // Start polling immediately on page load if not loaded
    if (Object.keys(hasLoadedMessages).length === 0) {
      fetchMessages();
    }
    
    const interval = setInterval(() => {
      if (isModalOpen) {
        // Refresh messages when modal is open (every 15 seconds)
        refreshMessages();
      }
      // TODO: Re-enable background polling when unread count logic is updated for multi-session
      // else {
      //   // Poll for new messages when modal is closed (every 30 seconds)
      //   fetchMessages();
      // }
    }, isModalOpen ? REFRESH_INTERVAL_OPEN : REFRESH_INTERVAL_CLOSED);
    
    return () => clearInterval(interval);
  }, [isModalOpen, moduleCode, discussionSessions.length, hasLoadedMessages, refreshMessages, fetchMessages]);
  
  // Focus input when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300); // Wait for modal animation
    }
  }, [isModalOpen]);
  
  // Calculate character count and validation
  const characterCount = newMessage.length;
  const isOverLimit = characterCount > MAX_MESSAGE_LENGTH;
  const canSend = newMessage.trim().length > 0 && !isOverLimit && !posting;
  
  // TODO: Update unread count logic for multi-session support
  // Calculate unread count based on timestamp (across all sessions)
  // const unreadCount = !isModalOpen && lastReadMessageTimestamp
  //   ? Object.values(messagesPerSession).flat().filter(message => {
  //       // Ensure both message and timestamp exist
  //       if (!message.timestamp || !lastReadMessageTimestamp) return false;
  //       
  //       const messageTime = new Date(message.timestamp);
  //       const lastReadTime = new Date(lastReadMessageTimestamp);
  //       
  //       // Validate dates are valid
  //       if (isNaN(messageTime.getTime()) || isNaN(lastReadTime.getTime())) return false;
  //       
  //       return messageTime > lastReadTime;
  //     }).length
  //   : 0;
  
  // Temporarily disable unread count until multi-session logic is implemented
  const unreadCount = 0;
  
  // Debug logging for unread count (can be removed in production)
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('Unread Count:', {
  //     count: unreadCount,
  //     condition: !isModalOpen && lastReadMessageTimestamp,
  //     modalOpen: isModalOpen,
  //     hasTimestamp: !!lastReadMessageTimestamp,
  //     totalMessages: Object.values(messagesPerSession).flat().length,
  //     discussionSessions: discussionSessions.length
  //   });
  // }
  
  return {
    // Modal state
    isModalOpen,
    openModal,
    openModalWithNewSession,
    closeModal,
    
    // Tab state
    discussionSessions,
    activeTabIndex,
    activeSession,
    switchToTab,
    
    // Messages state
    messages: activeMessages,
    messagesPerSession,
    loading,
    error,
    hasLoadedMessages,
    unreadCount,
    
    // Input state
    newMessage,
    handleMessageChange,
    handleKeyPress,
    inputRef,
    
    // Posting state
    posting,
    postMessage,
    canSend,
    
    // Character validation
    characterCount,
    isOverLimit,
    maxLength: MAX_MESSAGE_LENGTH,
    
    // Utility functions
    formatMessageTime,
    getUserInitials,
    scrollToBottom,
    messagesEndRef,
    
    // Actions
    refreshMessages,
    fetchMessages,
    updateMessage,
    deleteMessage
  };
};
